/**
 * Tudo que o app guarda no navegador passa por aqui.
 *
 * O padrão vem de um defeito observado em outro PWA desta máquina: o app
 * atualiza, o formato do que estava salvo muda, e o estado antigo continua lá.
 * A tela lê um campo que não existe mais, quebra, e o usuário não tem como
 * sair disso — recarregar não resolve, porque o lixo está no disco dele. O
 * conserto é uma constante de versão: se o que está salvo não foi escrito por
 * esta versão, apaga tudo e começa limpo.
 *
 * Perder o rascunho de uma simulação é barato. Deixar o app inutilizável num
 * celular a quarenta quilômetros da cidade não é.
 *
 * Três regras valem para todo acesso daqui:
 *
 * 1. **Toda leitura e escrita vai em try/catch.** Em aba anônima, com cookies
 *    de site bloqueados, ou com a cota estourada, o próprio acesso lança. Um
 *    app que não abre porque não conseguiu ler uma preferência de tema é pior
 *    que um app sem preferência de tema.
 * 2. **O que está aqui some.** `localStorage` é por navegador e por aparelho:
 *    não sincroniza, não chega ao servidor, e volta vazio depois de limpar
 *    dados. Serve para conveniência (último rascunho, tema escolhido, pacote
 *    de cálculo em cache) e nunca para o que não pode ser perdido.
 * 3. **Prefixo neutro.** As chaves nascem com `biogas:`. O nome de uma empresa
 *    numa chave de armazenamento sobrevive à empresa: em outro projeto desta
 *    máquina ainda existe um cookie com nome de cliente que já saiu do
 *    contrato, e renomear agora deslogaria todo mundo.
 */

/**
 * Suba este número ao mudar o FORMATO de qualquer coisa guardada.
 *
 * Mudança de formato é: renomear campo, trocar tipo, mudar unidade, passar a
 * guardar objeto onde era número. Acrescentar campo opcional, não.
 *
 * Histórico:
 *   1 — primeira versão (rascunho do simulador, tema, pacote de cálculo).
 */
export const VERSAO_DO_ARMAZENAMENTO = 1;

const PREFIXO = 'biogas:';
const CHAVE_DA_VERSAO = `${PREFIXO}versao`;

/** Memória do processo: o app funciona inteiro mesmo sem disco nenhum. */
const emMemoria = new Map();
let disponivel = null;

function deposito() {
  if (disponivel === null) {
    try {
      const sonda = `${PREFIXO}__sonda`;
      window.localStorage.setItem(sonda, '1');
      window.localStorage.removeItem(sonda);
      disponivel = true;
    } catch {
      // Aba anônima, cookies bloqueados, cota zerada. Nada disso é erro do
      // usuário, e nada disso pode derrubar o app.
      disponivel = false;
    }
  }
  return disponivel ? window.localStorage : null;
}

function nossasChaves(ls) {
  const chaves = [];
  for (let i = 0; i < ls.length; i += 1) {
    const k = ls.key(i);
    if (k && k.startsWith(PREFIXO)) chaves.push(k);
  }
  return chaves;
}

/**
 * Apaga o que este app guardou, sem tocar no que é de outro.
 *
 * O prefixo importa: em desenvolvimento, API e PWA dividem `localhost`, e um
 * `clear()` levaria junto o que não é nosso.
 */
export function limparTudo() {
  emMemoria.clear();
  const ls = deposito();
  if (!ls) return;
  try {
    for (const k of nossasChaves(ls)) ls.removeItem(k);
  } catch {
    /* ignorado de propósito: ver regra 1 */
  }
}

/**
 * Confere a versão do que está salvo e apaga tudo se não bater.
 *
 * Roda uma vez, antes do primeiro render. Devolve `true` quando houve limpeza
 * — quem chama pode avisar o usuário que o rascunho se perdeu, em vez de
 * deixá-lo procurar.
 */
export function conferirVersao() {
  const ls = deposito();
  if (!ls) return false;
  let salva;
  try {
    salva = ls.getItem(CHAVE_DA_VERSAO);
  } catch {
    return false;
  }
  if (salva === String(VERSAO_DO_ARMAZENAMENTO)) return false;

  const tinhaAlgo = (() => {
    try {
      return nossasChaves(ls).some((k) => k !== CHAVE_DA_VERSAO);
    } catch {
      return false;
    }
  })();

  limparTudo();
  try {
    ls.setItem(CHAVE_DA_VERSAO, String(VERSAO_DO_ARMAZENAMENTO));
  } catch {
    /* ignorado de propósito */
  }
  return tinhaAlgo;
}

/**
 * Lê um valor guardado. `padrao` quando não existe, não lê ou não é JSON.
 *
 * JSON corrompido devolve o padrão em vez de lançar: quem escreveu o lixo foi
 * uma versão anterior do app, e fazer o usuário pagar por isso com uma tela
 * branca é cobrar a dívida da pessoa errada.
 */
export function ler(chave, padrao = null) {
  const k = PREFIXO + chave;
  const ls = deposito();
  if (!ls) return emMemoria.has(k) ? emMemoria.get(k) : padrao;
  try {
    const cru = ls.getItem(k);
    if (cru === null) return padrao;
    return JSON.parse(cru);
  } catch {
    return padrao;
  }
}

/** Guarda um valor. Devolve `false` quando não deu — cota, aba anônima. */
export function guardar(chave, valor) {
  const k = PREFIXO + chave;
  emMemoria.set(k, valor);
  const ls = deposito();
  if (!ls) return false;
  try {
    ls.setItem(k, JSON.stringify(valor));
    return true;
  } catch {
    // Cota estourada é o caso real: o pacote de cálculo é o item grande. O
    // app segue com o valor em memória até fechar a aba.
    return false;
  }
}

/** Remove uma chave. */
export function esquecer(chave) {
  const k = PREFIXO + chave;
  emMemoria.delete(k);
  const ls = deposito();
  if (!ls) return;
  try {
    ls.removeItem(k);
  } catch {
    /* ignorado de propósito */
  }
}

/** Só para teste: faz a próxima chamada sondar o `localStorage` de novo. */
export function _esquecerSonda() {
  disponivel = null;
  emMemoria.clear();
}
